import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Pagination,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  DragIndicator as DragIndicatorIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import {
  DEFAULT_MANUAL_GROUP_NAME,
  type ManualGroupsTreeResponse,
  type ManualListItem,
  type ManualReadState,
  type ManualSearchResponse,
} from '../constants/manual';
import { formatPostTime } from '../utils/formatPostTime';

const manualSearchLimit = 20;

function manualReadStateLabel(state: ManualReadState): string | null {
  switch (state) {
    case 'unread':
      return '未読';
    case 'edited_since_read':
      return '編集あり';
    default:
      return null;
  }
}

type SortableManualHomeRowProps = {
  manual: ManualListItem;
  disabled: boolean;
  onOpen: () => void;
};

/** ドラッグで並び替え、クリックで詳細へ */
const SortableManualHomeRow: React.FC<SortableManualHomeRowProps> = ({ manual, disabled, onOpen }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `manual-${manual.id}`,
    disabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    ...(isDragging ? { zIndex: 1, position: 'relative' as const } : {}),
  };

  const stateLabel = manualReadStateLabel(manual.manual_read_state);
  const rowHighlight = manual.is_unread;

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        width: '100%',
        borderRadius: 1,
        ...(rowHighlight
          ? {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            }
          : {}),
      }}
    >
      <IconButton
        size="small"
        aria-label="マニュアルを並び替え"
        disabled={disabled}
        sx={{ cursor: disabled ? 'default' : 'grab', touchAction: 'none', flexShrink: 0 }}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon fontSize="small" color={disabled ? 'disabled' : 'action'} />
      </IconButton>
      <ListItemButton
        onClick={onOpen}
        sx={{
          flex: 1,
          minWidth: 0,
          py: 0.5,
          alignItems: 'center',
          ...(rowHighlight
            ? {
                '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04) },
              }
            : {}),
        }}
      >
        <ListItemText
          primary={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {manual.title}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  columnGap: 0.5,
                  rowGap: 0,
                  mt: 0.25,
                }}
              >
                <Typography component="span" variant="caption" color="text.secondary">
                  {manual.last_edited_by.display_name}
                </Typography>
                <Typography component="span" variant="caption" color="text.secondary">
                  · {formatPostTime(manual.updated_at)}
                </Typography>
                {stateLabel && (
                  <Typography
                    component="span"
                    sx={{
                      ml: 0.25,
                      px: 0.75,
                      py: 0.125,
                      borderRadius: 1,
                      fontSize: '0.65rem',
                      fontWeight: 400,
                      lineHeight: 1.3,
                      border: '1px solid',
                      borderColor:
                        manual.manual_read_state === 'unread' ? 'primary.main' : 'warning.main',
                      color: manual.manual_read_state === 'unread' ? 'primary.main' : 'warning.main',
                      bgcolor: 'transparent',
                    }}
                  >
                    {stateLabel}
                  </Typography>
                )}
              </Box>
            </Box>
          }
        />
      </ListItemButton>
    </Box>
  );
};

type ManualHomeCardProps = {
  /** 親が高さを与えるとき true（ホームのモバイルタブなど）。一覧エリアが内部スクロールになる */
  fillAvailableHeight?: boolean;
};

/**
 * ホーム右カラム: 社内マニュアル
 */
const ManualHomeCard: React.FC<ManualHomeCardProps> = ({ fillAvailableHeight = false }) => {
  const navigate = useNavigate();

  const [data, setData] = useState<ManualGroupsTreeResponse | null>(null);
  const [error, setError] = useState('');
  const [reordering, setReordering] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [searchData, setSearchData] = useState<ManualSearchResponse | null>(null);
  const [searchPage, setSearchPage] = useState(1);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const dataRef = useRef(data);
  dataRef.current = data;

  const searchActive = searchDebounced.length > 0;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const fetchList = useCallback(async () => {
    setError('');
    try {
      const res = await apiClient.get<ManualGroupsTreeResponse>('/api/manual/groups', {
        params: unreadOnly ? { unread_only: true } : undefined,
      });
      setData(res.data);
    } catch {
      setError('マニュアル一覧の取得に失敗しました');
      setData(null);
    }
  }, [unreadOnly]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  useEffect(() => {
    const id = window.setTimeout(() => setSearchDebounced(searchInput.trim()), 300);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  useLayoutEffect(() => {
    if (searchDebounced.length > 0) {
      setSearchPage(1);
    }
  }, [searchDebounced]);

  useLayoutEffect(() => {
    setSearchPage(1);
  }, [unreadOnly]);

  useEffect(() => {
    if (searchDebounced.length === 0) {
      setSearchData(null);
      setSearchError('');
      setSearchLoading(false);
      return;
    }
    let cancelled = false;
    setSearchLoading(true);
    setSearchError('');
    void apiClient
      .get<ManualSearchResponse>('/api/manual/search', {
        params: {
          q: searchDebounced,
          page: searchPage,
          limit: manualSearchLimit,
          ...(unreadOnly ? { unread_only: true } : {}),
        },
      })
      .then((res) => {
        if (!cancelled) setSearchData(res.data);
      })
      .catch(() => {
        if (!cancelled) {
          setSearchError('検索に失敗しました');
          setSearchData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setSearchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [searchDebounced, searchPage, unreadOnly]);

  const handleManualDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || reordering) return;

    const activeStr = String(active.id);
    const overStr = String(over.id);
    if (!activeStr.startsWith('manual-') || !overStr.startsWith('manual-')) return;

    const activeManualId = Number.parseInt(activeStr.replace(/^manual-/, ''), 10);
    const overManualId = Number.parseInt(overStr.replace(/^manual-/, ''), 10);
    if (!Number.isInteger(activeManualId) || !Number.isInteger(overManualId)) return;

    const previousTree = dataRef.current;
    if (!previousTree) return;

    const group = previousTree.groups.find((g) => g.manuals.some((m) => m.id === activeManualId));
    const groupOver = previousTree.groups.find((g) => g.manuals.some((m) => m.id === overManualId));
    if (!group || !groupOver || group.id !== groupOver.id) return;

    const oldIndex = group.manuals.findIndex((m) => m.id === activeManualId);
    const newIndex = group.manuals.findIndex((m) => m.id === overManualId);
    if (oldIndex < 0 || newIndex < 0) return;

    const reorderedManuals = arrayMove(group.manuals, oldIndex, newIndex);
    const withSort = reorderedManuals.map((m, i) => ({ ...m, sort_order: i }));
    const prevIds = group.manuals.map((m) => m.id);
    const newIds = withSort.map((m) => m.id);
    if (!prevIds.some((id, i) => id !== newIds[i])) return;

    const newGroups = previousTree.groups.map((g) =>
      g.id === group.id ? { ...g, manuals: withSort } : g,
    );

    setData({ groups: newGroups });
    setReordering(true);
    setError('');
    try {
      await apiClient.patch(`/api/manual/groups/${group.id}/manuals-order`, {
        manual_ids: withSort.map((m) => m.id),
      });
    } catch (e: unknown) {
      setData(previousTree);
      const msg =
        axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object'
          ? (e.response.data as { error?: string }).error
          : null;
      setError(msg || 'マニュアルの表示順の保存に失敗しました');
    } finally {
      setReordering(false);
    }
  };

  const manualListSection = (g: ManualGroupsTreeResponse['groups'][number]) => {
    if (g.manuals.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          このグループにはまだマニュアルがありません。
        </Typography>
      );
    }
    return (
      <SortableContext items={g.manuals.map((m) => `manual-${m.id}`)} strategy={verticalListSortingStrategy}>
        <List dense disablePadding>
          {g.manuals.map((m) => (
            <SortableManualHomeRow
              key={m.id}
              manual={m}
              disabled={reordering}
              onOpen={() => navigate(`/manual/${m.id}`)}
            />
          ))}
        </List>
      </SortableContext>
    );
  };

  const accordions = data?.groups.map((g) => (
    <Accordion
      key={g.id}
      defaultExpanded={g.name === DEFAULT_MANUAL_GROUP_NAME}
      disableGutters
      elevation={0}
      square
      sx={{
        boxShadow: 'none',
        border: 'none',
        borderRadius: 0,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ alignItems: 'center', '& .MuiAccordionSummary-content': { alignItems: 'center' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 0.5, rowGap: 0.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {g.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            （{g.manuals.length}件）
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>{manualListSection(g)}</AccordionDetails>
    </Accordion>
  ));

  return (
    <Card
      variant="outlined"
      elevation={0}
      sx={{
        height: fillAvailableHeight ? '100%' : { xs: 'auto', md: '100%' },
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: fillAvailableHeight ? 'hidden' : { md: 'hidden' },
        boxShadow: 'none',
      }}
    >
      <CardHeader
        title="社内マニュアル"
        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        sx={{ pb: 0, flexShrink: 0 }}
      />
      <CardContent
        sx={{
          pt: 1,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <TextField
          size="small"
          fullWidth
          label="検索"
          placeholder=""
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ mb: 2, flexShrink: 0 }}
        />

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 2,
            mb: 2,
            flexShrink: 0,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={unreadOnly}
                onChange={(_, checked) => setUnreadOnly(checked)}
                color="primary"
                size="small"
              />
            }
            label="未読のみ"
            sx={{ mr: 0 }}
          />
          {!searchActive && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<SettingsIcon />}
              onClick={() => navigate('/manual/groups')}
            >
              グループ管理
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate('/manual/new')}
          >
            新規マニュアル
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {searchActive && searchLoading && (
            <Typography variant="body2" color="text.secondary">
              検索中…
            </Typography>
          )}

          {searchActive && !searchLoading && searchError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {searchError}
            </Alert>
          )}

          {searchActive && !searchLoading && !searchError && searchData && searchData.results.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              該当するマニュアルがありません。
            </Typography>
          )}

          {searchActive && !searchLoading && !searchError && searchData && searchData.results.length > 0 && (
            <>
              <List dense disablePadding>
                {searchData.results.map((row) => {
                  const stateLabel = manualReadStateLabel(row.manual_read_state);
                  return (
                    <ListItemButton
                      key={row.id}
                      onClick={() => navigate(`/manual/${row.id}`)}
                      sx={{
                        borderRadius: 1,
                        ...(row.is_unread
                          ? {
                              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                              '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12) },
                            }
                          : {}),
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                              {row.title}
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                columnGap: 0.5,
                                rowGap: 0,
                                mt: 0.25,
                              }}
                            >
                              <Typography component="span" variant="caption" color="text.secondary">
                                {row.group_name}
                              </Typography>
                              <Typography component="span" variant="caption" color="text.secondary">
                                · {formatPostTime(row.updated_at)}
                              </Typography>
                              {stateLabel && (
                                <Typography
                                  component="span"
                                  sx={{
                                    ml: 0.25,
                                    px: 0.75,
                                    py: 0.125,
                                    borderRadius: 1,
                                    fontSize: '0.65rem',
                                    fontWeight: 400,
                                    lineHeight: 1.3,
                                    border: '1px solid',
                                    borderColor:
                                      row.manual_read_state === 'unread' ? 'primary.main' : 'warning.main',
                                    color: row.manual_read_state === 'unread' ? 'primary.main' : 'warning.main',
                                    bgcolor: 'transparent',
                                  }}
                                >
                                  {stateLabel}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  );
                })}
              </List>
              {searchData.total_pages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={Math.max(1, searchData.total_pages)}
                    page={searchPage}
                    onChange={(_, p) => setSearchPage(p)}
                    color="primary"
                    shape="rounded"
                  />
                </Box>
              )}
            </>
          )}

          {!searchActive && !data && !error && (
            <Typography variant="body2" color="text.secondary">
              読み込み中…
            </Typography>
          )}

          {!searchActive && data && data.groups.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              登録されているマニュアルグループがありません。
            </Typography>
          )}

          {!searchActive && data && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(e: DragEndEvent) => void handleManualDragEnd(e)}
            >
              {accordions}
            </DndContext>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ManualHomeCard;
